import apiClient from "./api-client";
import { handleApiError } from "./error-handler";
import { FreelancerOnboardingStatus } from "../models/enums/FreelancerOnboardingStatus";
import { FreelancerOnboardingFormStep } from "../models/enums/FreelancerOnboardingFormStep";
import { SubmitFreelancerOnboardingExperienceRatingRequest } from "../models/requests/SubmitFreelancerOnboardingExperienceRatingRequest";
import { FreelancerOnboardingExperienceRating } from "../models/FreelancerOnboardingExperienceRating";
import { FreelancerOnboardingStatusTrack } from "../models/FreelancerOnboardingStatusTrack";
import { FreelancerOnboardingSoftSkillsEvaluation } from "../models/FreelancerOnboardingSoftSkillsEvaluation";

/**
 * Update freelancer onboarding status
 * @param freelancerOnboardingId The ID of the freelancer onboarding
 * @param newStatus The new status to set
 */
export const updateFreelancerOnboardingStatus = async (
	freelancerOnboardingId: number,
	newStatus: FreelancerOnboardingStatus
): Promise<void> => {
	try {
		await apiClient.put(
			`/freelancer-onboardings/${freelancerOnboardingId}/current-status/${newStatus}`
		);
	} catch (error) {
		throw handleApiError(error);
	}
};

/**
 * Update freelancer onboarding forms progress current step
 * @param freelancerOnboardingId The ID of the freelancer onboarding
 * @param freelancerOnboardingFormsProgressId The ID of the freelancer onboarding forms progress
 * @param newStep The new current step to set
 */
export const updateFreelancerOnboardingFormsProgressCurrentStep = async (
	freelancerOnboardingId: number,
	freelancerOnboardingFormsProgressId: number,
	newStep: FreelancerOnboardingFormStep
): Promise<void> => {
	try {
		await apiClient.put(
			`/freelancer-onboardings/${freelancerOnboardingId}/forms-progress/${freelancerOnboardingFormsProgressId}/current-step/${newStep}`
		);
	} catch (error) {
		throw handleApiError(error);
	}
};

/**
 * Update freelancer onboarding expedited status
 * @param freelancerOnboardingId The ID of the freelancer onboarding
 * @param newIsOnboardingCurrentlyExpedited The new expedited status to set
 */
export const updateFreelancerOnboardingExpeditedStatus = async (
	freelancerOnboardingId: number,
	newIsOnboardingCurrentlyExpedited: boolean
): Promise<void> => {
	try {
		await apiClient.put(
			`/freelancer-onboardings/${freelancerOnboardingId}/is-onboarding-currently-expedited/${newIsOnboardingCurrentlyExpedited}`
		);
	} catch (error) {
		throw handleApiError(error);
	}
};

/**
 * Submit freelancer onboarding experience rating
 * @param freelancerOnboardingId The ID of the freelancer onboarding
 * @param rating The rating (1-5 stars)
 * @param feedback Optional feedback text
 */
export const submitFreelancerOnboardingExperienceRating = async (
	freelancerOnboardingId: number,
	rating: number,
	feedback?: string
): Promise<FreelancerOnboardingExperienceRating> => {
	try {
		const requestBody: SubmitFreelancerOnboardingExperienceRatingRequest = {
			rating,
			feedback: feedback
		};

		const response = await apiClient.post<FreelancerOnboardingExperienceRating>(
			`/freelancer-onboardings/${freelancerOnboardingId}/experience-ratings`,
			requestBody
		);

		return response.data;
	} catch (error) {
		throw handleApiError(error);
	}
};

export const GetFreelancerOnboardingStatusTracks = async (
	freelancerOnboardingId: number
): Promise<FreelancerOnboardingStatusTrack[]> => {
	try {
		const response = await apiClient.get(
			`/freelancer-onboarding-status-tracks?freelancerOnboardingId=${freelancerOnboardingId}`
		);

		return response.data;
	} catch (error) {
		throw handleApiError(error);
	}
};

export const GetFreelancerSoftSkillsEvaluations = async (
	freelancerOnboardingId: number
): Promise<FreelancerOnboardingSoftSkillsEvaluation[]> => {
	try {
		const response = await apiClient.get(
			`/freelancer-soft-skills-evaluations?freelancerOnboardingId=${freelancerOnboardingId}`
		);

		return response.data;
	} catch (error) {
		throw handleApiError(error);
	}
};
